import BottomNavBar from "../../components/BottomNavBar";
import { RightOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useEffect, useState } from "react";
import mealApiService from "../../services/MealApiService";
import {
  getMealBbsLabel,
  getMealBbsScore,
  getMealBbsTipReasons,
} from "../../utils/mealBbsScore";
import welcomeBg from "../../assets/homebg.png";
import {
  Coffee,
  Sun,
  Apple,
  Moon,
  Droplet,
  User,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import GluceraDateCalendar from "../../components/common/GluceraDateCalendar";
import { homeMetricIconPrimary } from "../../utils/homeBlueTheme";
import {
  gluceraCalorieGoal,
  gluceraDateCaption,
  gluceraGoalMeta,
  gluceraMacroLabel,
  gluceraMacroValue,
  gluceraMealCardTitle,
  gluceraMetricHero,
  gluceraMetricMd,
  gluceraMicroLabel,
  gluceraOutlineButtonLabel,
  gluceraPageSubtitle,
  measurePageTitle,
  gluceraRingPct,
  gluceraScoreDisplay,
  gluceraScoreEyebrow,
  measureEyebrow,
  measureSectionTitle,
} from "../../utils/gluceraTypography";

interface FoodItem {
  name: string;
  serving_size_g: number;
  serving_description: string;
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

interface MealTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

interface MealData {
  document_id?: string;
  id?: string;
  date: string;
  food_items: FoodItem[];
  totals: MealTotals;
  image_url?: string;
  image?: string;
  file_url?: string;
  document?: string;
  meal_type?: string;
  meal_name?: string;
  ghi_score?: number;
  ghi_label?: string;
  ghi_reasons?: string[];
  bbs_score?: number | null;
  bbs_label?: string | null;
  bbs_reasons?: string[];
  // Glucose spike fields from API
  spike_category?: string;
  this_meal_spike_mg_dl?: number;
  spike_band?: string;
  glucose_spike?: Record<string, any> | null;
}

interface GroupedMeals {
  breakfast: MealData[];
  lunch: MealData[];
  snack: MealData[];
  dinner: MealData[];
}

// Utility function to group meals by type based on meal_type field
const groupMealsByType = (meals: MealData[]): GroupedMeals => {
  const grouped: GroupedMeals = {
    breakfast: [],
    lunch: [],
    snack: [],
    dinner: [],
  };

  meals.forEach(meal => {
    if (meal.meal_type) {
      const type = meal.meal_type.toLowerCase();

      if (type === "breakfast") {
        grouped.breakfast.push(meal);
      } else if (type === "lunch") {
        grouped.lunch.push(meal);
      } else if (type === "snack" || type === "snacks") {
        grouped.snack.push(meal);
      } else if (type === "dinner") {
        grouped.dinner.push(meal);
      } else {
        grouped.snack.push(meal); // Default to snack for 'other'
      }
    }
  });

  return grouped;
};

const parseMealGoals = (raw: Record<string, unknown>) => ({
  calories_target: (raw.calories_target as number) ?? 2000,
  protein_g: (raw.protein_g as number) ?? 150,
  fat_g: (raw.fat_g as number) ?? 65,
  carbs_g: (raw.carbs_g as number) ?? 200,
  fiber_g: (raw.fiber_g as number) ?? 30,
  calories_target_completion_pct:
    typeof raw.calories_target_completion_pct === "number"
      ? raw.calories_target_completion_pct
      : null,
});

const formatGoalCompletionPct = (pct: number): string => String(Math.round(pct));

/** Meal log accents — subtle tints aligned with Measure / Profile cards */
const MEAL_LOG_TYPE_STYLE: Record<
  string,
  { gradient: string; iconWrap: string; iconClass: string }
> = {
  breakfast: {
    gradient:
      "from-amber-500/[0.08] via-amber-500/[0.02] to-transparent dark:from-amber-500/[0.12]",
    iconWrap:
      "border border-amber-200/70 bg-amber-50/90 dark:border-amber-900/45 dark:bg-amber-950/40",
    iconClass: "text-amber-600 dark:text-amber-400",
  },
  lunch: {
    gradient:
      "from-primary/[0.07] via-primary/[0.02] to-transparent dark:from-primary/[0.11]",
    iconWrap:
      "border border-sky-200/70 bg-sky-50/90 dark:border-sky-900/45 dark:bg-sky-950/35",
    iconClass: "text-primary",
  },
  snack: {
    gradient:
      "from-sky-500/[0.07] via-sky-500/[0.02] to-transparent dark:from-sky-500/[0.1]",
    iconWrap:
      "border border-border/80 bg-muted/50 dark:bg-muted/30",
    iconClass: "text-muted-foreground",
  },
  dinner: {
    gradient:
      "from-foreground/[0.04] via-foreground/[0.01] to-transparent dark:from-foreground/[0.06]",
    iconWrap:
      "border border-border/80 bg-muted/50 dark:bg-muted/30",
    iconClass: "text-foreground/70",
  },
};

const MealPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [todayBackendTotals, setTodayBackendTotals] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const getLocalDateString = (date: Date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Use date from homepage navigation state if provided, otherwise today
  const [selectedDate, setSelectedDate] = useState<string>(
    (location.state as any)?.selectedDate || getLocalDateString()
  );

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDateTotals, setSelectedDateTotals] = useState<any>(null);
  const [mealGoals, setMealGoals] = useState<any>({
    calories_target: 2000,
    protein_g: 150,
    fat_g: 65,
    carbs_g: 200,
    fiber_g: 30,
  });
  const [groupedMeals, setGroupedMeals] = useState<GroupedMeals>(() => {
    // Restore cached meals for this date on mount (back-navigation fast load)
    try {
      const cacheKey = `mealpage_cache_${(location.state as any)?.selectedDate || getLocalDateString()}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return { breakfast: [], lunch: [], snack: [], dinner: [] };
  });
  const [dailyGhi, setDailyGhi] = useState<any>(null);

  // Fetch meal data function
  const fetchMealDataForDate = async (dateString: string) => {
    try {
      const response = await mealApiService.getMealDataByDate(dateString);

      let mealsArray: any[] = [];
      let backendTotals = null;

      // Check if response itself is the data (not wrapped in response.data)
      const actualData = response?.data ?? response;

      if (actualData) {
        // Use backend provided totals directly
        if (
          actualData.total_net_carb !== undefined ||
          actualData.total_carbohydrate !== undefined ||
          actualData.total_protein !== undefined ||
          actualData.total_fat !== undefined ||
          actualData.total_fiber !== undefined ||
          actualData.total_calorie !== undefined
        ) {
          backendTotals = {
            calories: actualData.total_calorie ?? 0,
            carbs: actualData.total_carbohydrate ?? 0,
            net_carbs: actualData.total_net_carb ?? 0,
            protein: actualData.total_protein ?? 0,
            fat: actualData.total_fat ?? 0,
            fiber: actualData.total_fiber ?? 0,
          };
        }

        // Try multiple possible response structures
        if (actualData.meal_data && Array.isArray(actualData.meal_data)) {
          mealsArray = actualData.meal_data;
        } else if (actualData.results && Array.isArray(actualData.results)) {
          mealsArray = actualData.results;
        } else if (actualData.data && Array.isArray(actualData.data)) {
          mealsArray = actualData.data;
        } else if (Array.isArray(actualData)) {
          mealsArray = actualData as any;
        } else if (actualData.meals && Array.isArray(actualData.meals)) {
          mealsArray = actualData.meals;
        }

        // Transform meals to consistent format
        mealsArray = mealsArray.map((meal: any) => {
          // Build food_items: if empty but barcode flat data exists, construct from meal fields
          let foodItems = meal.food_items || [];
          const mealName = meal.meal_name || meal.product_name || meal.food_name || meal.item_name || meal.generic_name || "";
          if (foodItems.length === 0 && (mealName || meal.barcode)) {
            foodItems = [
              {
                name: mealName || meal.barcode || "Unknown Item",
                product_name: mealName || meal.barcode || "Unknown Item",
                source: "barcode",
                calorie_content_kcal: meal.calories || 0,
                protein_content_g: meal.protein || 0,
                carbohydrate_content_g: meal.carbohydrates || meal.carbs || 0,
                fat_content_g: meal.fat || 0,
                sugar_g: meal.sugar || 0,
                dietary_fiber_g: meal.fiber || 0,
                saturated_fat_g: meal.saturated_fat || 0,
                trans_fat_g: meal.trans_fat || 0,
                net_carbs_g: meal.net_carbs || 0,
                GI_estimate: meal.gi_estimate || 0,
                GL_estimate: meal.gl_estimate || 0,
                serving_size_g: meal.quantity || 100,
                serving_description: meal.quantity ? `${meal.quantity}` : "1 serving",
                barcode: meal.barcode || "",
                brand: meal.brand || "",
                uploaded_at: meal.date || "",
              },
            ];
          } else {
            // Normalize existing food_items: ensure each has a name field
            foodItems = foodItems.map((item: any) => ({
              ...item,
              name: item.name || item.food_name || item.item_name || item.product_name || mealName || "Unknown Item",
            }));
          }

          return {
          ...meal,
          meal_type: meal.meal_type, // Preserve meal_type from API
          meal_name: meal.meal_name || meal.meal_title, // Preserve meal_name/meal_title from API
          totals: meal.totals || {
            carbohydrate_content_g: 0,
            dietary_fiber_g: 0,
            net_carbs_g: 0,
            sugar_g: 0,
            protein_content_g: 0,
            fat_content_g: 0,
            trans_fat_g: 0,
            saturated_fat_g: 0,
            calorie_content_kcal: 0,
            GI_estimate: 0,
            GL_estimate: 0,
          },
          food_items: foodItems,
          image_url: meal.image_url ?? meal.document,
          date: meal.date,
          ghi_score: meal.ghi_score || 0,
          ghi_label: meal.ghi_label || "",
          bbs_score: getMealBbsScore(meal),
          bbs_label: getMealBbsLabel(meal),
          bbs_reasons: getMealBbsTipReasons(meal),
          spike_category: meal.spike_category ?? meal.glucose_spike?.spike_category,
          this_meal_spike_mg_dl: meal.this_meal_spike_mg_dl ?? meal.glucose_spike?.this_meal_spike_mg_dl,
          spike_band: meal.spike_band ?? meal.glucose_spike?.spike_band,
          glucose_spike: meal.glucose_spike ?? null,
          };
        });
      }

      return {
        meals: mealsArray,
        totals: backendTotals,
        goals: actualData?.meal_goals
          ? parseMealGoals(actualData.meal_goals as Record<string, unknown>)
          : null,
        apiDate: actualData.date_local,
        dailyGhi: actualData.daily_ghi ?? null,
      };
    } catch (error: any) {
      return { meals: [], totals: null, goals: null, apiDate: null, dailyGhi: null };
    }
  };

  const [apiDate, setApiDate] = useState<string | null>(null);

  useEffect(() => {
    const loadTodayData = async () => {
      setLoading(true);

      const today = getLocalDateString();

      const todayResult = await fetchMealDataForDate(today);

      setTodayBackendTotals(todayResult.totals);

      if (todayResult.dailyGhi) {
        setDailyGhi(todayResult.dailyGhi);
      }

      if (todayResult.apiDate) {
        setApiDate(todayResult.apiDate);
      }

      // Set meal goals from API
      if (todayResult.goals) {
        setMealGoals(todayResult.goals);
      }

      // Set initial selected date totals from backend
      const initialTotals = todayResult.totals ?? {
        calories: 0,
        carbs: 0,
        protein: 0,
        fat: 0,
        fiber: 0,
      };
      setSelectedDateTotals(initialTotals);

      // Direct API call to ensure meals load properly
      try {
        const directResponse = await mealApiService.getMealDataByDate(today);

        if (
          directResponse?.meal_data &&
          Array.isArray(directResponse.meal_data)
        ) {
          const transformedMeals = directResponse.meal_data.map((meal: any) => {
            let foodItems = meal.food_items || [];
            const mealName = meal.meal_name || meal.product_name || meal.food_name || meal.item_name || meal.generic_name || "";
            if (foodItems.length === 0 && (mealName || meal.barcode)) {
              foodItems = [{
                name: mealName || meal.barcode || "Unknown Item",
                product_name: mealName || meal.barcode || "Unknown Item",
                source: "barcode",
                calorie_content_kcal: meal.calories || 0,
                protein_content_g: meal.protein || 0,
                carbohydrate_content_g: meal.carbohydrates || meal.carbs || 0,
                fat_content_g: meal.fat || 0,
                sugar_g: meal.sugar || 0,
                dietary_fiber_g: meal.fiber || 0,
                saturated_fat_g: meal.saturated_fat || 0,
                trans_fat_g: meal.trans_fat || 0,
                net_carbs_g: meal.net_carbs || 0,
                GI_estimate: meal.gi_estimate || 0,
                GL_estimate: meal.gl_estimate || 0,
                serving_size_g: meal.quantity || 100,
                serving_description: meal.quantity ? `${meal.quantity}` : "1 serving",
                barcode: meal.barcode || "",
                brand: meal.brand || "",
                uploaded_at: meal.date || "",
              }];
            } else {
              foodItems = foodItems.map((item: any) => ({
                ...item,
                name: item.name || item.food_name || item.item_name || item.product_name || mealName || "Unknown Item",
              }));
            }
            return { 
              ...meal, 
              meal_name: meal.meal_name || meal.meal_title, // Preserve meal_name/meal_title from API
              food_items: foodItems,
              spike_category: meal.spike_category ?? meal.glucose_spike?.spike_category,
              this_meal_spike_mg_dl: meal.this_meal_spike_mg_dl ?? meal.glucose_spike?.this_meal_spike_mg_dl,
              spike_band: meal.spike_band ?? meal.glucose_spike?.spike_band,
              glucose_spike: meal.glucose_spike ?? null,
            };
          });
          const directGrouped = groupMealsByType(transformedMeals);
          setGroupedMeals(directGrouped);

          // Set totals from direct response
          if (directResponse.total_calorie !== undefined) {
            const directTotals = {
              calories: directResponse.total_calorie ?? 0,
              carbs: directResponse.total_carbohydrate ?? 0,
              net_carbs: directResponse.total_net_carb ?? 0,
              protein: directResponse.total_protein ?? 0,
              fat: directResponse.total_fat ?? 0,
              fiber: directResponse.total_fiber ?? 0,
            };
            setTodayBackendTotals(directTotals);
            setSelectedDateTotals(directTotals);
          }

          // Set goals from direct response
          if (directResponse.meal_goals) {
            setMealGoals(
              parseMealGoals(
                directResponse.meal_goals as Record<string, unknown>
              )
            );
          }

          // Extract daily_ghi from direct response
          if (directResponse.daily_ghi) {
            setDailyGhi(directResponse.daily_ghi);
          }

          setLoading(false);
          return;
        }
      } catch (directError) {
        // Silent fail, use fallback
      }

      // Fallback - Set initial grouped meals for today
      const grouped = groupMealsByType(todayResult.meals);
      setGroupedMeals(grouped);
      setSelectedDate(today);
      setLoading(false);
    };

    loadTodayData();
  }, []);

  const todayTotals = todayBackendTotals ?? {
    calories: 0,
    carbs: 0,
    net_carbs: 0,
    protein: 0,
    fat: 0,
    fiber: 0,
  };

  const getTotalsForDate = (dateString: string) => {
    if (selectedDateTotals) {
      return selectedDateTotals;
    }

    const today = getLocalDateString();
    if (dateString === today) {
      return todayTotals;
    }
    return {
      calories: 0,
      carbs: 0,
      net_carbs: 0,
      protein: 0,
      fat: 0,
      fiber: 0,
    };
  };

  const handleMealTypeClick = (mealType: string, meals: MealData[]) => {
    if (meals.length === 0) {
      // Navigate to food scanning if no meals
      navigate(`/glucera${GluceraNavigationRoutes.HOMEPAGE}`, {
        state: {
          mealType: mealType.charAt(0).toUpperCase() + mealType.slice(1),
          fromMealPage: true,
        },
      });
      return;
    }

    if (meals.length === 1) {
      // If only one meal, go directly to detail
      navigate(`/glucera${GluceraNavigationRoutes.mealdetailpage}`, {
        state: {
          mealId: meals[0].document_id,
          mealNumber: 1,
          mealType: mealType.charAt(0).toUpperCase() + mealType.slice(1),
          mealName: meals[0].meal_name,
          mealItems: meals[0].food_items,
          mealTotals: meals[0].totals,
          mealDate: meals[0].date,
          mealImage: meals[0].image_url ?? meals[0].document,
          ghiScore: meals[0].ghi_score,
          ghiLabel: meals[0].ghi_label,
          ghiReasons: meals[0].ghi_reasons ?? [],
          bbsScore: getMealBbsScore(meals[0] as Record<string, unknown>),
          bbsLabel: getMealBbsLabel(meals[0] as Record<string, unknown>),
          bbsReasons: getMealBbsTipReasons(meals[0] as Record<string, unknown>),
          dailyGhi: dailyGhi,
          fromMealPage: true,
          glucoseSpikeData: (meals[0] as any)?.glucose_spike ?? null,
          source: (meals[0].food_items?.[0] as any)?.source === "barcode" ? "barcode" : undefined,
        },
      });
    } else {
      // Multiple meals - navigate to meal detail with all meals of this type
      const aggregatedTotals = meals.reduce(
            (acc, meal) => ({
              carbohydrate_content_g:
                acc.carbohydrate_content_g +
                (meal.totals.carbohydrate_content_g || 0),
              dietary_fiber_g:
                acc.dietary_fiber_g + (meal.totals.dietary_fiber_g || 0),
              net_carbs_g: acc.net_carbs_g + (meal.totals.net_carbs_g || 0),
              sugar_g: acc.sugar_g + (meal.totals.sugar_g || 0),
              protein_content_g:
                acc.protein_content_g + (meal.totals.protein_content_g || 0),
              fat_content_g:
                acc.fat_content_g + (meal.totals.fat_content_g || 0),
              trans_fat_g: acc.trans_fat_g + (meal.totals.trans_fat_g || 0),
              saturated_fat_g:
                acc.saturated_fat_g + (meal.totals.saturated_fat_g || 0),
              calorie_content_kcal:
                acc.calorie_content_kcal +
                (meal.totals.calorie_content_kcal || 0),
              GI_estimate: acc.GI_estimate + (meal.totals.GI_estimate || 0),
              GL_estimate: acc.GL_estimate + (meal.totals.GL_estimate || 0),
            }),
            {
              carbohydrate_content_g: 0,
              dietary_fiber_g: 0,
              net_carbs_g: 0,
              sugar_g: 0,
              protein_content_g: 0,
              fat_content_g: 0,
              trans_fat_g: 0,
              saturated_fat_g: 0,
              calorie_content_kcal: 0,
              GI_estimate: 0,
              GL_estimate: 0,
            }
          );
      
      // Calculate average GHI score for multiple meals
      const avgGhiScore = meals.filter(m => m.ghi_score).length > 0
        ? meals.reduce((sum, m) => sum + (m.ghi_score || 0), 0) / meals.filter(m => m.ghi_score).length
        : undefined;
      
      navigate(`/glucera${GluceraNavigationRoutes.mealdetailpage}`, {
        state: {
          mealType: mealType.charAt(0).toUpperCase() + mealType.slice(1),
          mealName: meals[0]?.meal_name,
          mealItems: meals.flatMap(meal => meal.food_items),
          mealTotals: aggregatedTotals,
          mealDate: meals[0]?.date,
          ghiScore: avgGhiScore,
          ghiLabel: meals[0]?.ghi_label,
          ghiReasons: meals[0]?.ghi_reasons ?? [],
          bbsScore: getMealBbsScore(meals[0] as Record<string, unknown>),
          bbsLabel: getMealBbsLabel(meals[0] as Record<string, unknown>),
          bbsReasons: getMealBbsTipReasons(meals[0] as Record<string, unknown>),
          dailyGhi: dailyGhi,
          fromMealPage: true,
          allMeals: meals,
          glucoseSpikeData: (meals[0] as any)?.glucose_spike ?? null,
          source: meals.every(m => (m.food_items?.[0] as any)?.source === "barcode") ? "barcode" : undefined,
        },
      });
    }
  };

  // Date handling functions
  const handleDateSelect = async (dateString: string) => {
    // Check if selected date is in the future
    // Use T00:00:00 suffix to force local-time parsing (bare ISO strings are parsed as UTC)
    const selectedDateObj = new Date(dateString + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDateObj.setHours(0, 0, 0, 0);

    if (selectedDateObj > today) {
      return;
    }

    setSelectedDate(dateString);
    setApiDate(null); // Clear previous API date to show selected date immediately
    setIsCalendarOpen(false);
    setLoading(true);

    try {
      const response = await mealApiService.getMealDataByDate(dateString);

      // Check different response structures
      const actualData = response?.data ?? response;

      let mealsArray = [];
      let totalsData = null;

      // Extract meals array
      if (actualData?.meal_data && Array.isArray(actualData.meal_data)) {
        mealsArray = actualData.meal_data;
      } else if (actualData?.results && Array.isArray(actualData.results)) {
        mealsArray = actualData.results;
      } else if (Array.isArray(actualData)) {
        mealsArray = actualData;
      } else if (actualData?.meals && Array.isArray(actualData.meals)) {
        mealsArray = actualData.meals;
      }

      // Transform meals to preserve meal_name and other properties
      mealsArray = mealsArray.map((meal: any) => {
        const mealName = meal.meal_name || meal.product_name || meal.food_name || meal.item_name || meal.generic_name || "";
        let foodItems = meal.food_items || [];
        if (foodItems.length === 0 && (mealName || meal.barcode)) {
          foodItems = [{
            name: mealName || meal.barcode || "Unknown Item",
            product_name: mealName || meal.barcode || "Unknown Item",
            source: "barcode",
            calorie_content_kcal: meal.calories || 0,
            protein_content_g: meal.protein || 0,
            carbohydrate_content_g: meal.carbohydrates || meal.carbs || 0,
            fat_content_g: meal.fat || 0,
            sugar_g: meal.sugar || 0,
            dietary_fiber_g: meal.fiber || 0,
            saturated_fat_g: meal.saturated_fat || 0,
            trans_fat_g: meal.trans_fat || 0,
            net_carbs_g: meal.net_carbs || 0,
            GI_estimate: meal.gi_estimate || 0,
            GL_estimate: meal.gl_estimate || 0,
            serving_size_g: meal.quantity || 100,
            serving_description: meal.quantity ? `${meal.quantity}` : "1 serving",
            barcode: meal.barcode || "",
            brand: meal.brand || "",
          }];
        } else {
          foodItems = foodItems.map((item: any) => ({
            ...item,
            name: item.name || item.food_name || item.item_name || item.product_name || mealName || "Unknown Item",
          }));
        }
        return {
          ...meal,
          meal_name: meal.meal_name || meal.meal_title,
          food_items: foodItems,
          spike_category: meal.spike_category ?? meal.glucose_spike?.spike_category,
          this_meal_spike_mg_dl: meal.this_meal_spike_mg_dl ?? meal.glucose_spike?.this_meal_spike_mg_dl,
          spike_band: meal.spike_band ?? meal.glucose_spike?.spike_band,
          glucose_spike: meal.glucose_spike ?? null,
        };
      });

      // Extract totals
      if (actualData) {
        totalsData = {
          calories: actualData.total_calorie ?? actualData.total_calories ?? 0,
          carbs: actualData.total_carbohydrate ?? 0,
          net_carbs: actualData.total_net_carb ?? 0,
          protein: actualData.total_protein ?? 0,
          fat: actualData.total_fat ?? 0,
          fiber: actualData.total_fiber ?? 0,
        };
      }

      // Group meals by type
      const groupedMealsForDate = groupMealsByType(mealsArray);

      // Cache in sessionStorage for instant back-navigation load
      try {
        sessionStorage.setItem(`mealpage_cache_${dateString}`, JSON.stringify(groupedMealsForDate));
      } catch { /* ignore quota errors */ }

      // Update state
      setGroupedMeals(groupedMealsForDate);
      setSelectedDateTotals(totalsData);
      setApiDate(actualData.date_local || dateString);

      // Extract daily_ghi from date change response
      if (actualData?.daily_ghi) {
        setDailyGhi(actualData.daily_ghi);
      } else {
        setDailyGhi(null);
      }

      // Update goals if available
      if (actualData?.meal_goals) {
        setMealGoals(
          parseMealGoals(actualData.meal_goals as Record<string, unknown>)
        );
      }
    } catch (error) {
      // Set empty state if API call fails
      setGroupedMeals({
        breakfast: [],
        lunch: [],
        snack: [],
        dinner: [],
      });
      setSelectedDateTotals({
        calories: 0,
        carbs: 0,
        net_carbs: 0,
        protein: 0,
        fat: 0,
        fiber: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === "prev") {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  // Helper functions for meal UI
  const getMealIcon = (mealType: string, iconClass: string) => {
    const cls = `h-[22px] w-[22px] shrink-0 ${iconClass}`;
    switch (mealType.toLowerCase()) {
      case "breakfast":
        return <Coffee className={cls} strokeWidth={1.85} aria-hidden />;
      case "lunch":
        return <Sun className={cls} strokeWidth={1.85} aria-hidden />;
      case "snack":
        return <Apple className={cls} strokeWidth={1.85} aria-hidden />;
      case "dinner":
        return <Moon className={cls} strokeWidth={1.85} aria-hidden />;
      default:
        return <Apple className={cls} strokeWidth={1.85} aria-hidden />;
    }
  };


  const getGlucoseSpike = (meal: MealData) => {
    const category = meal.spike_category;
    const value = meal.this_meal_spike_mg_dl;
    if (!category || value == null) return null;

    const getCategoryStyle = (cat: string) => {
      switch (cat.toLowerCase()) {
        case "low":
          return {
            bg: "bg-green-50 dark:bg-green-950/30",
            text: "text-green-700 dark:text-green-400",
            label: "Low Spike",
          };
        case "moderate":
        case "medium":
          return {
            bg: "bg-orange-50 dark:bg-orange-950/30",
            text: "text-orange-700 dark:text-orange-400",
            label: "Moderate Spike",
          };
        case "high":
          return {
            bg: "bg-red-50 dark:bg-red-950/30",
            text: "text-red-700 dark:text-red-400",
            label: "High Spike",
          };
        default:
          return {
            bg: "bg-gray-50 dark:bg-gray-950/30",
            text: "text-gray-700 dark:text-gray-400",
            label: category,
          };
      }
    };

    const style = getCategoryStyle(category);

    return (
      <div className={`mt-2 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 ${style.bg}`}>
        <Droplet className={`h-3.5 w-3.5 ${style.text}`} />
        <span className={`text-caption1 font-semibold ${style.text}`}>
          {style.label} (+{value} mg/dL)
        </span>
      </div>
    );
  };

  const getMealTime = (_mealType: string, meals: MealData[]) => {
    if (meals.length === 0) {
      return ""; // Don't show any time if no meal exists
    }

    // Convert time to 12-hour format
    try {
      const rawDate = meals[0].date;

      // Parse different date/time formats
      if (typeof rawDate === "string") {
        let timeString = "";

        // Extract time part if it's a full datetime string
        if (rawDate.includes(" ") && rawDate.includes(":")) {
          timeString = rawDate.split(" ")[1]; // Get time part after space
        } else if (rawDate.includes("T") && rawDate.includes(":")) {
          timeString = rawDate.split("T")[1].split("Z")[0]; // ISO format
        } else if (rawDate.includes(":") && !rawDate.includes("-")) {
          timeString = rawDate; // Already just time
        } else {
          // Parse as full date and extract time
          const date = new Date(rawDate);
          return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }

        // Convert 24-hour to 12-hour format
        if (timeString.includes(":")) {
          const [hours, minutes] = timeString.split(":");
          const hour24 = parseInt(hours);
          const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
          const ampm = hour24 >= 12 ? "PM" : "AM";
          return `${hour12}:${minutes.padStart(2, "0")} ${ampm}`;
        }
      }

      // Fallback - parse as date object
      const date = new Date(rawDate);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return ""; // Don't show time if parsing fails
    }
  };

  const intakeTotalsForHero = getTotalsForDate(selectedDate);
  const calHeroCurrent = Math.round(intakeTotalsForHero.calories);
  const calCompletionPct =
    typeof mealGoals.calories_target_completion_pct === "number" &&
    Number.isFinite(mealGoals.calories_target_completion_pct)
      ? mealGoals.calories_target_completion_pct
      : mealGoals.calories_target > 0
        ? (calHeroCurrent / mealGoals.calories_target) * 100
        : 0;


  return (
    <div className="scrollbar-hide relative mx-auto flex h-dvh max-h-dvh w-full min-w-0 max-w-[400px] flex-col overflow-hidden bg-background">
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{ backgroundImage: `url(${welcomeBg})` }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-primary/10 via-amber-500/[0.04] to-transparent dark:from-primary/15 dark:via-amber-500/[0.06]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-35 dark:opacity-25"
        style={{
          backgroundImage:
            "radial-gradient(hsl(var(--primary) / 0.12) 1px, transparent 1px)",
          backgroundSize: "14px 14px",
        }}
        aria-hidden
      />

      <div className="scrollbar-hide relative z-10 min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-28 pt-safe">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 pb-1">
          <div className="min-w-0">
            <p className={gluceraPageSubtitle}>
              {selectedDate === getLocalDateString() ? "Today" : "Summary"}
            </p>
            <h1 className={measurePageTitle}>
              {selectedDate === getLocalDateString()
                ? "Your meals"
                : "Meal summary"}
            </h1>
            <p className={cn("mt-0.5", gluceraDateCaption)}>
              {apiDate
                ? new Date(apiDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  timeZone: "UTC",
                })
                : new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
            </p>
          </div>
          <div className="flex items-center gap-2">
          <GluceraDateCalendar
            open={isCalendarOpen}
            onOpenChange={setIsCalendarOpen}
            currentMonth={currentMonth}
            onCurrentMonthChange={setCurrentMonth}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
          />
          {/* Profile Button — rightmost corner */}
          <button
            type="button"
            onClick={() => navigate("/glucera/profilepage")}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card hover:bg-muted"
            aria-label="Profile"
          >
            <User className={cn("h-5 w-5", homeMetricIconPrimary)} />
          </button>
          </div>
        </div>


        {/* Today's intake — Measure-style cards */}
        <div className="space-y-3">
          <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-primary/[0.07] via-primary/[0.02] to-transparent dark:from-primary/[0.1]" />
            <div className="relative p-3 sm:p-3.5">
              <p className={`mb-1 ${measureEyebrow}`}>
                {selectedDate === getLocalDateString() ? "Today's intake" : "Daily intake"}
              </p>
              <div className="flex items-end justify-between gap-3">
                <div>
                  <p className={gluceraMacroLabel}>
                    Calories
                  </p>
                  <div className="mt-0.5 flex items-end gap-1 tabular-nums">
                    <span className={gluceraMetricHero}>
                      {calHeroCurrent}
                    </span>
                    <span className={gluceraCalorieGoal}>
                      / {mealGoals.calories_target} cal
                    </span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <span className={cn(gluceraMetricHero, "text-primary")}>
                    {formatGoalCompletionPct(calCompletionPct)}%
                  </span>
                  <p className={gluceraGoalMeta}>of goal</p>
                </div>
              </div>
            </div>
          </div>

          {/* Macros — unified primary rings (no rainbow) */}
          <div className="grid grid-cols-2 gap-3">
            {([
              {
                label: "Protein",
                value: getTotalsForDate(selectedDate).protein,
                goal: mealGoals.protein_g,
                color: "#3b82f6",   // blue
                bgFrom: "from-blue-500/[0.06]",
              },
              {
                label: "Carbs",
                value: getTotalsForDate(selectedDate).carbs,
                goal: mealGoals.carbs_g,
                color: "#f59e0b",   // amber
                bgFrom: "from-amber-500/[0.06]",
              },
              {
                label: "Fat",
                value: getTotalsForDate(selectedDate).fat,
                goal: mealGoals.fat_g,
                color: "#ef4444",   // red
                bgFrom: "from-red-500/[0.06]",
              },
              {
                label: "Fiber",
                value: getTotalsForDate(selectedDate).fiber,
                goal: mealGoals.fiber_g,
                color: "#22c55e",   // green
                bgFrom: "from-green-500/[0.06]",
              },
            ]).map((m) => {
              const radius = 28;
              const stroke = 5;
              const normR = radius - stroke / 2;
              const C = 2 * Math.PI * normR;
              const rawPct = m.goal ? (m.value / m.goal) * 100 : 0;
              const clampedPct = Math.min(Math.max(rawPct, 0), 100);
              const offset = C - (clampedPct / 100) * C;
              return (
                <div
                  key={m.label}
                  className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60"
                >
                  <div className={`pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b ${m.bgFrom} to-transparent`} />
                  <div className="relative flex items-center gap-3 p-3 sm:p-3.5">
                    <div className="relative h-14 w-14 shrink-0 rounded-full bg-muted/35 p-1 ring-1 ring-border/55 dark:bg-muted/25">
                      <svg
                        width="100%"
                        height="100%"
                        viewBox={`0 0 ${radius * 2} ${radius * 2}`}
                        className="rotate-[-90deg]"
                      >
                        <circle
                          cx={radius}
                          cy={radius}
                          r={normR}
                          fill="none"
                          stroke={m.color}
                          strokeOpacity={0.15}
                          strokeWidth={stroke}
                        />
                        <circle
                          cx={radius}
                          cy={radius}
                          r={normR}
                          fill="none"
                          stroke={m.color}
                          strokeWidth={stroke}
                          strokeLinecap="round"
                          strokeDasharray={C}
                          strokeDashoffset={offset}
                          style={{ transition: "stroke-dashoffset 0.7s ease-out" }}
                        />
                      </svg>
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <span className={gluceraRingPct}>
                          {Math.round(rawPct)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <p className={gluceraMacroLabel}>
                        {m.label}
                      </p>
                      <p className={cn("mt-0.5", gluceraMacroValue)}>
                        {Math.round(m.value)}g
                      </p>
                      <p className={gluceraGoalMeta}>
                        Goal: {m.goal}g
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-primary/[0.06] to-transparent dark:from-primary/[0.1]" />
              <div className="relative p-3 text-center sm:p-3.5">
                <div className={gluceraMetricMd}>
                  {Object.values(groupedMeals).flat().length}
                </div>
                <div className={`mt-1 ${measureEyebrow}`}>
                  Meals
                </div>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-emerald-500/[0.06] to-transparent dark:from-emerald-500/[0.1]" />
              <div className="relative p-3 text-center sm:p-3.5">
                <div className={gluceraMetricMd}>
                  {Math.round(getTotalsForDate(selectedDate).net_carbs || 0)}g
                </div>
                <div className={`mt-1 ${measureEyebrow}`}>
                  Net carbs
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Meal Log Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className={measureEyebrow}>Activity</p>
              <h3 className={`mt-0.5 ${measureSectionTitle}`}>Meal log</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9 shrink-0 rounded-xl border border-border/80 bg-card px-3.5 text-xs font-bold uppercase tracking-wider text-muted-foreground shadow-sm hover:bg-muted/80"
              onClick={() =>
                navigate(`/glucera${GluceraNavigationRoutes.MEAL_HISTORY}`)
              }
            >
              View History
            </Button>
          </div>

          {/* Meal Type Cards */}
          <div className="space-y-3">
            {/* Render individual meal cards for each meal */}
            {[
              ...groupedMeals.breakfast.map((meal, index) => ({
                type: "breakfast",
                label: "Breakfast",
                meal: meal,
                index: index,
              })),
              ...groupedMeals.lunch.map((meal, index) => ({
                type: "lunch",
                label: "Lunch",
                meal: meal,
                index: index,
              })),
              ...groupedMeals.snack.map((meal, index) => ({
                type: "snack",
                label: "Snack",
                meal: meal,
                index: index,
              })),
              ...groupedMeals.dinner.map((meal, index) => ({
                type: "dinner",
                label: "Dinner",
                meal: meal,
                index: index,
              })),
              // Add empty meal cards if no meals exist for that type
              ...(groupedMeals.breakfast.length === 0
                ? [
                  {
                    type: "breakfast",
                    label: "Breakfast",
                    meal: null,
                    index: 0,
                  },
                ]
                : []),
              ...(groupedMeals.lunch.length === 0
                ? [{ type: "lunch", label: "Lunch", meal: null, index: 0 }]
                : []),
              ...(groupedMeals.snack.length === 0
                ? [{ type: "snack", label: "Snack", meal: null, index: 0 }]
                : []),
              ...(groupedMeals.dinner.length === 0
                ? [{ type: "dinner", label: "Dinner", meal: null, index: 0 }]
                : []),
            ]
              .sort((a, b) => {
                const order: Record<string, number> = {
                  breakfast: 0,
                  lunch: 1,
                  snack: 2,
                  dinner: 3,
                };
                return order[a.type] - order[b.type] || a.index - b.index;
              })
              .map(({ type, label, meal, index }) => {
                const ms =
                  MEAL_LOG_TYPE_STYLE[type] ?? MEAL_LOG_TYPE_STYLE.snack;
                return (
                <div
                  key={`${type}-${index}`}
                  onClick={
                    meal ? () => handleMealTypeClick(type, [meal]) : undefined
                  }
                  className={`relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm transition-all dark:border-border/60 ${meal
                    ? "cursor-pointer hover:shadow-md active:scale-[0.99]"
                    : "cursor-default opacity-60"
                    }`}
                >
                  <div
                    className={`pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b ${ms.gradient}`}
                    aria-hidden
                  />
                  <div className="relative p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${meal ? ms.iconWrap : "border border-border/80 bg-muted/50"}`}
                      >
                        {getMealIcon(type, meal ? ms.iconClass : "text-muted-foreground")}
                      </div>
                      <div className="min-w-0 flex-1 space-y-1">
                        <h4 className={gluceraMealCardTitle}>
                          {label}
                        </h4>
                        {meal?.meal_name && (
                          <span className="inline-block rounded-lg bg-muted/60 px-2 py-0.5 text-footnote font-semibold text-foreground">
                            {meal.meal_name}
                          </span>
                        )}
                        <p className="text-footnote font-medium text-muted-foreground">
                          {meal ? getMealTime(type, [meal]) : "No meal logged"}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {meal && getMealBbsScore(meal as Record<string, unknown>) != null && (() => {
                        const mealDisplayScore = Math.round(getMealBbsScore(meal as Record<string, unknown>)!);
                        return (
                        <div className="flex flex-col items-end gap-0.5">
                            <span className={gluceraScoreEyebrow}>
                              Meal score
                            </span>
                            <div
                              className={`flex items-baseline gap-1 rounded-xl px-2.5 py-1.5 ${
                                mealDisplayScore >= 80
                                  ? "bg-emerald-50 ring-1 ring-emerald-200/90 dark:bg-emerald-950/40 dark:ring-emerald-800/50"
                                  : mealDisplayScore >= 50
                                    ? "bg-amber-50 ring-1 ring-amber-200/90 dark:bg-amber-950/40 dark:ring-amber-800/50"
                                    : "bg-orange-50 ring-1 ring-orange-200/90 dark:bg-orange-950/40 dark:ring-orange-800/50"
                              }`}
                            >
                              <span
                                className={cn(
                                  gluceraScoreDisplay,
                                  "leading-none",
                                  mealDisplayScore >= 80
                                    ? "text-emerald-600 dark:text-emerald-400"
                                    : mealDisplayScore >= 50
                                      ? "text-amber-600 dark:text-amber-400"
                                      : "text-orange-600 dark:text-orange-400"
                                )}
                              >
                                {mealDisplayScore}
                              </span>
                              <span className="text-footnote font-semibold text-muted-foreground">
                                /100
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                      <RightOutlined className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                  </div>

                  {meal && (
                    <div className="mt-4 space-y-3 border-t border-border/60 pt-4">
                      {getGlucoseSpike(meal)}
                      {meal.food_items && meal.food_items.length > 0 && (
                        <div className="space-y-2">
                          <p className={gluceraMicroLabel}>
                            Items
                          </p>
                          <div className="space-y-1.5">
                            {meal.food_items.slice(0, 3).map((item, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/35" />
                                <span className="min-w-0 flex-1 text-footnote font-semibold leading-snug text-foreground">
                                  {(item as any).food_name ||
                                    (item as any).item_name ||
                                    (item as any).product_name ||
                                    item.name ||
                                    "Unknown Item"}
                                </span>
                              </div>
                            ))}
                            {meal.food_items.length > 3 && (
                              <p className="text-footnote font-semibold text-muted-foreground">
                                +{meal.food_items.length - 3} more items
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
                );
              })}
          </div>
        </div>
      </div>

      <BottomNavBar activeIndex={1} />
    </div>
  );
};

export default MealPage;
